import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Typography,
} from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';

interface IMenuToggleItem {
  key: string;
  label: string;
  icon: ReactNode;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  /** Nur Einträge, die der Nutzer laut Berechtigung + aktiver Erweiterung
   * überhaupt sehen darf. */
  items: IMenuToggleItem[];
  /** Aktuell ausgeblendete MenuKeys (kann auch derzeit nicht sichtbare
   * enthalten — die bleiben unangetastet erhalten). */
  hidden: string[];
  onSave: (hidden: string[]) => Promise<void> | void;
  saving?: boolean;
}

/**
 * Dialog „Menüeinstellungen": pro Menüpunkt ein Schalter (an = sichtbar).
 * Zeigt ausschließlich die für den Nutzer erlaubten Einträge.
 */
export const MenuSettingsDialog = ({
  open,
  onClose,
  items,
  hidden,
  onSave,
  saving,
}: IProps) => {
  const [local, setLocal] = useState<Set<string>>(new Set(hidden));

  useEffect(() => {
    if (open) {
      setLocal(new Set(hidden));
    }
  }, [open, hidden]);

  const toggle = (key: string) =>
    setLocal(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>Menüeinstellungen</DialogTitle>
      <DialogContent dividers>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Wählen Sie, welche Menüpunkte im linken Menü angezeigt werden.
          Ausgeblendete Punkte erreichen Sie weiterhin über „Ausgeblendete
          Elemente".
        </Typography>
        <List>
          {items.map(item => (
            <ListItem
              key={item.key}
              secondaryAction={
                <Switch
                  edge='end'
                  checked={!local.has(item.key)}
                  onChange={() => toggle(item.key)}
                />
              }
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          disabled={saving}
          onClick={async () => {
            await onSave(Array.from(local));
            onClose();
          }}
        >
          {saving ? <CircularProgress size={20} /> : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
